<template>
  <div class="page-api-keys">
    <nav class="breadcrumb mt-5" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/admin">Admin Settings</router-link></li>
        <li class="is-active"><router-link to="/dashboard/api-key-management" aria-current="true">API Key Management</router-link></li>
      </ul>
    </nav>
    <div class="container">
      <div class="columns is-multiline">
        <div class="column is-12">
          <h1 class="title">API Key Management</h1>
          <button class="button is-primary" @click="openCreateModal">Create API Key</button>
        </div>

        <div v-if="showBanner" class="column is-12">
          <div class="notification is-warning">
            <button class="delete" @click="closeBanner"></button>
            <strong>Copy your new API key:</strong>
            <p>{{ newApiKey }}</p>
            <p>This key cannot be viewed again.</p>
          </div>
        </div>

        <div class="columns" style="gap: 15px; margin-top: 10px; padding-left: 20px">
          <input 
            v-model="filters.name" 
            type="text" 
            class="input is-rounded is-small" 
            placeholder="Search by Name"
          >
          <label class="checkbox is-small">
            <input v-model="filters.hasExpired" type="checkbox">
            Has Expired
          </label>
          <label class="checkbox is-small">
            <input v-model="filters.revoked" type="checkbox">
            Revoked
          </label>
          <div class="select is-link is-small">
            <select v-model.number="itemsPerPage">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        <div class="column is-12">
          <table class="table is-fullwidth">
            <thead>
              <tr>
                <th class="is-size-7">Name</th>
                <th class="is-size-7">Created</th>
                <th class="is-size-7">Expires</th>
                <th class="is-size-7">Has Expired</th>
                <th class="is-size-7">Revoked</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="apiKey in filteredApiKeys" 
                :key="apiKey.id" 
                class="api-key-row"
                @click="openEditModal(apiKey)"
              >
                <td class="is-size-7">{{ apiKey.name }}</td>
                <td class="is-size-7">{{ formatDate(apiKey.created) }}</td>
                <td class="is-size-7">{{ apiKey.expires ? formatDate(apiKey.expires) : 'Never' }}</td>
                <td class="is-size-7">{{ apiKey.has_expired ? 'Yes' : 'No' }}</td>
                <td class="is-size-7">{{ apiKey.revoked ? 'Yes' : 'No' }}</td>
              </tr>
            </tbody>
          </table>
          <div class="pagination">
            <button @click="prevPage" :disabled="currentPage === 1" class="button is-ghost is-small">Previous</button>
            <span class="strong is-size-7">Page {{ currentPage }} of {{ totalPages }}</span>
            <button @click="nextPage" :disabled="currentPage === totalPages" class="button is-ghost is-small">Next</button>
          </div>
        </div>
      </div>
    </div>

    <create-edit-api-key-modal
      v-if="isModalOpen"
      :is-edit="isEdit"
      :api-key="selectedApiKey"
      @close="closeModal"
      @submit="handleModalSubmit"
      @delete="deleteApiKey"
    ></create-edit-api-key-modal>
  </div>
</template>

<script>
import axios from 'axios';
import CreateEditApiKeyModal from './CreateEditApiKeyModal.vue';
import { toast } from 'bulma-toast';

export default {
  components: {
    CreateEditApiKeyModal,
  },
  data() {
    return {
      apiKeys: [],
      isModalOpen: false,
      isEdit: false,
      selectedApiKey: null,
      showBanner: false,
      newApiKey: '',
      currentPage: 1,
      itemsPerPage: 10,
      filters: {
        name: '',
        hasExpired: false,
        revoked: false,
      },
    };
  },
  computed: {
    filteredApiKeys() {
      let filtered = this.apiKeys;

      // Filter by name
      if (this.filters.name) {
        filtered = filtered.filter(key =>
          key.name.toLowerCase().includes(this.filters.name.toLowerCase())
        );
      }

      // Filter by has_expired
      if (this.filters.hasExpired) {
        filtered = filtered.filter(key => key.has_expired === true);
      }

      // Filter by revoked
      if (this.filters.revoked) {
        filtered = filtered.filter(key => key.revoked === true);
      }

      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return filtered.slice(start, end);
    },
    totalPages() {
      let filtered = this.apiKeys;

      if (this.filters.name) {
        filtered = filtered.filter(key =>
          key.name.toLowerCase().includes(this.filters.name.toLowerCase())
        );
      }

      if (this.filters.hasExpired) {
        filtered = filtered.filter(key => key.has_expired === true);
      }

      if (this.filters.revoked) {
        filtered = filtered.filter(key => key.revoked === true);
      }

      return Math.ceil(filtered.length / this.itemsPerPage);
    },
  },
  methods: {
    openCreateModal() {
      this.isEdit = false;
      this.selectedApiKey = null;
      this.isModalOpen = true;
    },
    openEditModal(apiKey) {
      this.isEdit = true;
      this.selectedApiKey = { ...apiKey };
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    handleModalSubmit(apiKey) {
      if (this.isEdit) {
        this.updateApiKey(apiKey);
      } else {
        this.createApiKey(apiKey);
      }
    },
    createApiKey(apiKey) {
      axios.post('/api/v1/authentication/api-keys/', apiKey, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        }
      })
      .then(response => {
        this.newApiKey = response.data.key;
        this.showBanner = true;
        toast({ message: 'API Key created successfully', type: 'is-success' });
        this.closeModal();
        this.loadApiKeys();
      })
      .catch(error => {
        toast({ message: 'Failed to create API key', type: 'is-danger' });
        console.error(error);
      });
    },
    updateApiKey(apiKey) {
      axios.put(`/api/v1/authentication/api-keys/${apiKey.id}/`, apiKey, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`,
        }
      })
      .then(response => {
        const index = this.apiKeys.findIndex(key => key.id === apiKey.id);
        if (index !== -1) {
          this.$set(this.apiKeys, index, response.data);
        }
        toast({ message: 'API Key updated successfully', type: 'is-success' });
        this.closeModal();
      })
      .catch(error => {
        toast({ message: 'Failed to update API key', type: 'is-danger' });
        console.error(error);
      });
    },
    deleteApiKey(apiKeyId) {
      if (confirm('Are you sure you want to delete this API Key?')) {
        axios.delete(`/api/v1/authentication/api-keys/${apiKeyId}/`, {
          headers: {
            'Authorization': `Token ${localStorage.getItem('token')}`,
          }
        })
        .then(() => {
          this.apiKeys = this.apiKeys.filter(apiKey => apiKey.id !== apiKeyId);
          toast({ message: 'API Key deleted successfully', type: 'is-success' });
          this.closeModal();
        })
        .catch(error => {
          toast({ message: 'Failed to delete API key', type: 'is-danger' });
          console.error(error);
        });
      }
    },
    formatDate(date) {
      const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(date).toLocaleDateString(undefined, options);
    },
    closeBanner() {
      this.showBanner = false;
    },
    loadApiKeys() {
      axios.get('/api/v1/authentication/api-keys/?limit=1000', {
        headers: {
          'Authorization': `Token ${localStorage.getItem('token')}`,
        }
      })
      .then(response => {
        this.apiKeys = response.data.results || response.data;
        this.currentPage = 1;
      })
      .catch(error => {
        toast({ message: 'Failed to load API keys', type: 'is-danger' });
        console.error(error);
      });
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
  },
  created() {
    this.loadApiKeys();
  },
};
</script>

<style scoped>
.api-key-row {
  cursor: pointer;
}

.api-key-row:hover {
  background-color: #f5f5f5;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  gap: 1rem;
}

.pagination button {
  margin: 0;
}

.checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  white-space: nowrap;
}

.checkbox input {
  margin: 0;
}
</style>
